import React, { useEffect, useMemo } from 'react';
import { Space, Segmented, Select, Switch, Typography } from 'antd';
import type { HistoryGranularity } from '../types';
import { compareSnapshotsDesc } from '../utils/snapshot';

const { Text } = Typography;

export interface HistoryControlsValue {
  enabled: boolean;
  granularity: HistoryGranularity;
  from?: string;
  to?: string;
}

interface Props {
  enabled: boolean;
  granularity: HistoryGranularity;
  snapshots: string[];
  from?: string;
  to?: string;
  onChange: (v: HistoryControlsValue) => void;
}

const HistoryControls: React.FC<Props> = ({ enabled, granularity, snapshots, from, to, onChange }) => {
  // 保险：前端也按字符串降序排列（YYYY-MM[-DD] 可直接比较），确保索引0=最新
  const sortedSnapshots = useMemo(() => {
    return (snapshots || []).slice().sort(compareSnapshotsDesc);
  }, [snapshots]);

  // 默认选择最近两次：索引0=今天，索引1=昨天
  const defaults = useMemo(() => {
    if (!sortedSnapshots || sortedSnapshots.length < 1) return { from, to };
    const latest = sortedSnapshots[0];
    const previous = sortedSnapshots.length >= 2 ? sortedSnapshots[1] : undefined;
    return {
      from: from || previous,
      to: to || latest,
    };
  }, [sortedSnapshots, from, to]);

  useEffect(() => {
    // 若开启对比：默认填充最近两次；未开启则默认仅填充最近一次（to）
    if (enabled) {
      if (!from || !to) {
        onChange({ enabled, granularity, from: defaults.from, to: defaults.to });
      }
    } else {
      if (!to && defaults.to) {
        onChange({ enabled, granularity, from, to: defaults.to });
      }
    }
  }, [enabled, granularity, defaults.from, defaults.to]);

  return (
    <Space size={8} wrap>
      <Text type="secondary">对比</Text>
      <Switch
        checked={enabled}
        onChange={(v) => onChange({ enabled: v, granularity, from, to })}
        size="small"
      />
      <Segmented
        size="small"
        options={[{label:'日',value:'day'},{label:'周',value:'week'},{label:'月',value:'month'}]}
        value={granularity}
        onChange={(v) => onChange({ enabled, granularity: v as HistoryGranularity, from, to })}
      />
      {enabled ? (
        <>
          <Select
            size="small"
            style={{ width: 140 }}
            placeholder="起点快照"
            value={from}
            onChange={(v) => onChange({ enabled, granularity, from: v, to })}
            options={sortedSnapshots.map(s => ({ label: s, value: s }))}
            allowClear
          />
          <Select
            size="small"
            style={{ width: 140 }}
            placeholder="目标快照"
            value={to}
            onChange={(v) => onChange({ enabled, granularity, from, to: v })}
            options={sortedSnapshots.map(s => ({ label: s, value: s }))}
            allowClear
          />
        </>
      ) : (
        <Select
          size="small"
          style={{ width: 160 }}
          placeholder="选择快照"
          value={to}
          onChange={(v) => onChange({ enabled, granularity, from, to: v })}
          options={sortedSnapshots.map(s => ({ label: s, value: s }))}
          allowClear
        />
      )}
    </Space>
  );
};

export default HistoryControls;
